import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

import SimpleLayout from "@/layouts/SimpleLayout.vue";
import DashboardLayout from "@/layouts/DashboardLayout.vue";
import { useSession } from "@/stores/session";
import { AuthAPI } from "@/api";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: DashboardLayout,
    children: [
      // irma chat & files
      {
        path: "/irma",
        component: () => import("@/views/irma").then((m) => m.IrmaView),
      },
      {
        path: "/irma/files",
        component: () => import("@/views/irma").then((m) => m.IrmaFilesView),
      },

      // organisations
      {
        path: "/organisations/graph",
        component: () =>
          import("@/views/organisations").then((m) => m.OrganisationGraphView),
      },
      {
        path: "/organisations/new",
        component: () =>
          import("@/views/organisations").then((m) => m.OrganisationEditView),
      },
      {
        path: "/organisations",
        component: () =>
          import("@/views/organisations").then((m) => m.OrganisationListView),
      },
      {
        path: "/organisations/:id",
        component: () =>
          import("@/views/organisations").then(
            (m) => m.OrganisationDetailsView
          ),
      },
      {
        path: "/organisations/:id/edit",
        component: () =>
          import("@/views/organisations").then((m) => m.OrganisationEditView),
      },

      // contexts
      {
        path: "/contexts",
        component: () =>
          import("@/views/contexts").then((m) => m.ContextListView),
      },
      {
        path: "/contexts/new",
        component: () =>
          import("@/views/contexts").then((m) => m.ContextEditView),
      },
      {
        path: "/contexts/:id",
        component: () =>
          import("@/views/contexts").then((m) => m.ContextDetailsView),
      },
      {
        path: "/contexts/:id/edit",
        component: () =>
          import("@/views/contexts").then((m) => m.ContextEditView),
      },

      // risks
      {
        path: "/",
        component: () => import("@/views/risks").then((m) => m.RiskListView),
      },
      {
        path: "/risks",
        component: () => import("@/views/risks").then((m) => m.RiskListView),
      },
      {
        path: "/risks/map",
        component: () => import("@/views/risks/RiskMapView.vue"),
      },
      {
        path: "/risks/new",
        component: () => import("@/views/risks").then((m) => m.RiskEditView),
      },

      {
        path: "/risks/:id",
        component: () => import("@/views/risks").then((m) => m.RiskEditView),
      },

      // causes
      {
        path: "/causes",
        component: () => import("@/views/causes").then((m) => m.CauseListView),
      },
      {
        path: "/causes/new",
        component: () => import("@/views/causes").then((m) => m.CauseEditView),
      },
      {
        path: "/causes/:id",
        component: () =>
          import("@/views/causes").then((m) => m.CauseDetailsView),
      },
      {
        path: "/causes/:id/edit",
        component: () => import("@/views/causes").then((m) => m.CauseEditView),
      },

      // measures
      {
        path: "/measures",
        component: () =>
          import("@/views/measures").then((m) => m.MeasureListView),
      },
      {
        path: "/measures/new",
        component: () =>
          import("@/views/measures").then((m) => m.MeasureCreateView),
      },
      {
        path: "/risks/:id/measures",
        name: "MeasureEdit",
        component: () =>
          import("@/views/measures").then((m) => m.MeasureEditView),
      },

      // analyse
      {
        path: "/analyse",
        component: () => import("@/views/analyse").then((m) => m.AnalyseView),
      },
      {
        path: "/analyse/save-risks",
        component: () => import("@/views/analyse").then((m) => m.SaveRisksView),
      },

      // users
      {
        path: "/users",
        component: () => import("@/views/users").then((m) => m.UserListView),
      },
      {
        path: "/users/new",
        component: () => import("@/views/users").then((m) => m.UserEditView),
      },
      {
        path: "/users/:id",
        component: () => import("@/views/users").then((m) => m.UserDetailsView),
      },
      {
        path: "/users/:id/edit",
        component: () => import("@/views/users").then((m) => m.UserEditView),
      },

      // branches
      {
        path: "/branches",
        component: () =>
          import("@/views/branches").then((m) => m.BranchListView),
      },
      {
        path: "/branches/new",
        component: () =>
          import("@/views/branches").then((m) => m.BranchEditView),
      },
      {
        path: "/branches/:id",
        component: () =>
          import("@/views/branches").then((m) => m.BranchDetailsView),
      },
      {
        path: "/branches/:id/edit",
        component: () =>
          import("@/views/branches").then((m) => m.BranchEditView),
      },

      // assessment
      {
        path: "/assessments",
        name: "AssessmentList",
        component: () =>
          import("@/views/assessment").then((m) => m.AssessmentListView),
      },
      {
        path: "/assessments/create",
        name: "AssessmentCreate",
        component: () =>
          import("@/views/assessment").then((m) => m.AssessmentCreateView),
      },
      {
        path: "/assessments/:id",
        name: "AssessmentDetail",
        component: () =>
          import("@/views/assessment").then((m) => m.AssessmentDetailView),
        props: true,
      },
    ],
    meta: { requiresAuth: true },
  },
  {
    path: "/",
    component: SimpleLayout,
    children: [
      { path: "login", component: () => import("@/views/LoginView.vue") },
      { path: "assess", component: () => import("@/views/AssessView.vue") },
    ],
    meta: { requiresAuth: false },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const { isAuthorized } = useSession();
  if (to.meta.requiresAuth && !isAuthorized.value) {
    await AuthAPI.refresh();
    if (isAuthorized.value) {
      next();
    } else next("/login");
  } else {
    next();
  }
});

router.afterEach((to) => {
  if (to.path === "/login" && !sessionStorage.getItem("loginReloaded")) {
    sessionStorage.setItem("loginReloaded", "true");
    window.location.reload();
  }
});

export default router;
